import type { InternalLink, Price, productAPI } from '@backmarket/http-api'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import { type Estimation } from '~/scopes/buyback/api/adapters/getEstimationSwapPrice.adapter'
import { SWAP_SUCCESS } from '~/scopes/buyback/swap/components/SwapBlock/constants'

import { MODAL_NAMES } from '../../../constants'
import translations from '../CustomizationFunnel.translations'

export const mapOption = (
  option: productAPI.GetPickersResponse['pickers'][number]['items'][number],
): Option => {
  return {
    ...option,
    link: !isEmpty(option.link)
      ? { ...option.link, hash: { ...option.link.hash, scroll: 'false' } }
      : undefined,
  }
}

const mapSimSubLabel = (option: Option): Option => {
  const key = `sim_${option.trackingValue}`

  return {
    ...option,
    subLabel: translations[key] ? translations[key] : '',
  }
}

type Option = Omit<
  productAPI.GetPickersResponse['pickers'][number]['items'][number],
  'price' | 'productId' | 'label'
> & {
  label: I18nDefinition | string
  productId?: string
  price?: Price | string
  value?: string
  subLabel?: I18nDefinition | string
  tag?: I18nDefinition
}

export type Step = {
  desktopIllustration?: string
  id: string
  title: I18nDefinition<string>
  titleEmphasis: I18nDefinition<string>
  trackingId?: string
  type?: string
  options: Option[] | []
  guidance?: {
    label: I18nDefinition<string>
    illustration: string
    modalName: string
  }
}

/**
 * This function is here to map the response of existing endpoints (pickers, best_offers) to
 * something easily usable by the Customization Funnel.
 *
 * We chose to reuse existing endpoints over a new dedicated one to gain some time developing the
 * MVP that will be A/B tested on smartphones only. This is not meant to be scaled to any other category
 * without a new endpoint to be created.
 */
export const createCustomizationFunnelData = (
  pickers: productAPI.GetPickersResponse['pickers'],
  product: productAPI.GetProductResponse,
  route: InternalLink,
  swapStatus: string,
  swapOffer?: Estimation,
): Step[] => {
  const pickersGrade = pickers.find(({ id }) => id === 'grades')
  const pickersStorage = pickers.find(({ id }) => id === 'storage')
  const pickersColor = pickers.find(({ id }) => id === 'color')
  const pickersSim = pickers.find(({ id }) => id === 'dual_sim')
  const pickersBattery = pickers.find(({ id }) => id === 'battery')

  const isNewBatteryAvailable = pickersBattery?.items?.[1]?.available ?? false

  const steps: Array<Step | null> = pickers
    .map((group) => {
      switch (group.id) {
        case 'grades':
          return {
            id: 'grade',
            title: translations.titleStepGrade,
            titleEmphasis: translations.titleEmphasisStepGrade,
            trackingId: pickersGrade?.trackingId,
            options: pickersGrade?.items.map(mapOption) ?? [],
          }
        case 'battery':
          return {
            id: 'battery',
            desktopIllustration: '/img/product/funnel/step-battery.svg',
            title: isNewBatteryAvailable
              ? translations.titleStepBattery
              : translations.titleStepBatteryDisabled,
            titleEmphasis: isNewBatteryAvailable
              ? translations.titleEmphasisStepBattery
              : translations.titleEmphasisStepBatteryDisabled,
            trackingId: 'new_battery',
            options: [
              {
                ...mapOption(
                  pickersBattery
                    ?.items[0] as productAPI.GetPickersResponse['pickers'][number]['items'][number],
                ),
                label: translations.newBatteryOptionNo,
                subLabel: translations.newBatteryOptionNoDescription,
                trackingValue: 'deactivate',
              },
              {
                ...mapOption(
                  pickersBattery
                    ?.items[1] as productAPI.GetPickersResponse['pickers'][number]['items'][number],
                ),
                label: translations.newBatteryOptionYes,
                subLabel: translations.newBatteryOptionYesDescription,
                trackingValue: 'activate',
              },
            ],
            guidance: {
              label: translations.helpCardBattery,
              illustration: '/img/product/funnel/step-battery.svg',
              modalName: MODAL_NAMES.NEW_BATTERY,
            },
          }
        case 'dual_sim':
          return {
            id: 'sim',
            title: translations.titleStepSim,
            titleEmphasis: translations.titleEmphasisStepSim,
            trackingId: pickersSim?.trackingId,
            options: pickersSim?.items.map(mapOption).map(mapSimSubLabel) || [],
            desktopIllustration: '/img/product/sim-modal.svg',
            guidance: {
              label: translations.helpCardSim,
              illustration: '/img/product/funnel/step-sim.svg',
              modalName: MODAL_NAMES.SIM,
            },
          }
        case 'storage':
          return {
            id: 'storage',
            title: translations.titleStepStorage,
            titleEmphasis: translations.titleEmphasisStepStorage,
            trackingId: pickersStorage?.trackingId,
            options: pickersStorage?.items.map(mapOption) ?? [],
            desktopIllustration: '/img/product/funnel/step-storage.svg',
          }
        case 'color':
          return {
            id: 'color',
            title: translations.titleStepColor,
            titleEmphasis: translations.titleEmphasisStepColor,
            columns: 2,
            trackingId: pickersColor?.trackingId,
            options: pickersColor?.items.map(mapOption) ?? [],
          }
        default:
          return null
      }
    })
    .filter(Boolean)

  if (product.isSwapEligible) {
    const isSwapInCart =
      swapStatus === SWAP_SUCCESS && Boolean(swapOffer?.hasOffer)

    steps.push({
      id: 'tradein',
      type: 'service',
      title: translations.titleStepTradeIn,
      titleEmphasis: translations.titleEmphasisStepTradeIn,
      trackingId: 'swap',
      desktopIllustration: '/img/product/funnel/step-trade-in.svg',
      guidance: {
        label: translations.tradeInGuidance,
        illustration: '/img/product/funnel/guidance-trade-in.svg',
        modalName: MODAL_NAMES.TRADE_IN,
      },
      options: [
        {
          value: 'off',
          label: translations.tradeInOptionNo,
          trackingValue: 'no',
          available: true,
          // No is selected is no trade in offer is accepted. This means that we change the value only
          // if the user goes through the whole flow and ACCEPTS the offer
          selected: !isSwapInCart,
          // We need to have a link prop to have the right button styling, but trade in has absolutely
          // no impact on the URL, so we keep the current route as href.
          link: route,
        },
        {
          value: 'on',
          label: translations.tradeInOptionYes,
          // subLabel and tag values changes if the user accepted the offer.
          subLabel: isSwapInCart
            ? translations.tradeInOptionYesDescriptionAccepted
            : translations.tradeInOptionYesDescription,
          tag: isSwapInCart
            ? translations.tradeInTagAccepted
            : translations.tradeInTag,
          price: isSwapInCart ? swapOffer?.offerPrice : undefined,
          trackingValue: 'yes',
          available: true,
          selected: isSwapInCart,
          link: route,
        },
      ],
    })
  }

  return steps as unknown as Step[]
}
